import { useState } from 'react'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import { UpdateLaborCostConceptsDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { dispatch } from 'modules/core/lib/pubSub'
import app from 'modules/core/store/models/app'

import { useExtraData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/ExtraData'
import { PAYROLL_SOURCES_EVENT } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/components/Filters/DataSourceSelector/const'

interface Props {
  onClose: () => void
}

export const PayrollModal = ({ onClose }: Props) => {
  const [changed, setChanged] = useState(false)
  const {
    extraData: {
      payrollConcepts: {
        concepts: data,
        currentAssignedLaborCost,
        loading,
        refetch: refetchPayrollConcepts,
      },
    },
  } = useExtraData()

  const [selectedValues, setSelectedValues] = useState<number[]>([])

  const [updateLaborCostConcepts, { loading: updateLoading }] = useMutation(
    UpdateLaborCostConceptsDocument,
    {
      extractorFn: (newData) =>
        newData.payrollMutations.updateLaborCostPayrollLaborCostConfiguration,
    }
  )

  const handleUpdate = async () => {
    const result = await updateLaborCostConcepts({
      variables: {
        ids: selectedValues,
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      refetchPayrollConcepts()

      dispatch({
        type: PAYROLL_SOURCES_EVENT,
        data: {
          concept: {
            id: selectedValues[0],
            name: formattedConcepts.find((concept) => concept.value === selectedValues[0])?.label,
          },
        },
      })
      onClose()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }

    return handled
  }

  const formattedConcepts = useUnsafeMemo(() => {
    return (data || []).map((node) => ({
      label: node.translatedName || node.label || '',
      value: node.id,
      laborCost: node.laborCost,
    }))
  }, [data])

  useUnsafeEffect(() => {
    const valuesWithLaborCost = formattedConcepts.find((concept) => concept.laborCost)

    if (!valuesWithLaborCost) {
      setSelectedValues([currentAssignedLaborCost?.id ?? 0])
    } else {
      setSelectedValues([valuesWithLaborCost.value])
    }
  }, [formattedConcepts, currentAssignedLaborCost])

  const handleChange = (value: number) => {
    if (
      (value === 0 && selectedValues[0] !== currentAssignedLaborCost?.id) ||
      (value !== 0 && value !== selectedValues[0])
    ) {
      setChanged(true)
    }

    if (value === 0) {
      setSelectedValues([currentAssignedLaborCost?.id ?? 0])
      return
    }

    setSelectedValues([value])
  }

  return (
    <Modal
      title={i18n.t('finance.financial_workspace.employee_costs.labor_costs.update_modal.title')}
      description={i18n.t(
        'finance.financial_workspace.employee_costs.labor_costs.update_modal.desc'
      )}
      style={{ width: '456px' }}
      illustration="document-edit"
      onClose={onClose}
      onSubmit={() => {}}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button
            submit
            disabled={loading || !changed || updateLoading}
            onClick={() => {
              handleUpdate()
            }}
          >
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Box gap="s12">
        {loading && (
          <Box alignItems="center" padding="s16" justifyContent="center">
            <Spinner />
          </Box>
        )}
        {!loading && (
          <Selector.Popover
            label={i18n.t(
              'finance.financial_workspace.employee_costs.labor_costs.update_modal.select_title'
            )}
            searchPlaceholder={i18n.t('common.search')}
            placeholder={i18n.t('common.select_basic')}
            blankLabel={i18n.t('common.empty')}
            value={selectedValues}
            isDisabled={updateLoading}
            isMultiSelect={false}
            onChange={(value) => {
              handleChange(Number(value))
            }}
          >
            {formattedConcepts.map((option) => (
              <Selector.Option
                key={option.value}
                value={option.value}
                label={option.label}
                isMultiSelect={false}
                searchValue={option.label}
              >
                {option.label}
              </Selector.Option>
            ))}
          </Selector.Popover>
        )}
      </Box>
    </Modal>
  )
}
